import { getEnvVariable } from '@/lib/utils/env';
import { SSR_DEBUG_ALLOWED_ENVIRONMENTS } from '@/lib/utils/ssr-debug/ssr-debug.consts';
import { STORE_OVERRIDE_COOKIE } from './store-override.consts';

// RFC 1123 labels, at least two of them. The value ends up in the gateway request
// path and in a header the render trusts, so anything else is refused, not repaired.
const HOSTNAME_REGEX =
    /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

// NEXT_PUBLIC_ENVIRONMENT is inlined at build time, so production and sandbox
// builds can never open this gate, whatever cookie or query a request carries.
// Not via @web-store-platforms/dev-tools: middleware imports this, and that barrel ships React.
export const isStoreOverrideEnvironmentAllowed = (): boolean => {
    const environment = getEnvVariable('NEXT_PUBLIC_ENVIRONMENT') || getEnvVariable('REACT_APP_ENV');
    return (
        environment !== undefined &&
        (SSR_DEBUG_ALLOWED_ENVIRONMENTS as readonly string[]).includes(environment)
    );
};

export const normalizeStoreOverride = (rawValue: string | null | undefined): string | null => {
    if (rawValue === null || rawValue === undefined) {
        return null;
    }
    const domain = rawValue.trim().toLowerCase().replace(/^www\./, '');
    return HOSTNAME_REGEX.test(domain) ? domain : null;
};

/** The store a cookie value names, or null where overriding is not allowed. */
export const resolveStoreOverride = (cookieValue: string | undefined): string | null => {
    if (!isStoreOverrideEnvironmentAllowed()) {
        return null;
    }
    return normalizeStoreOverride(cookieValue);
};

export const readStoreOverrideFromCookieHeader = (cookieHeader: string): string | null => {
    const prefix = `${STORE_OVERRIDE_COOKIE}=`;
    const entry = cookieHeader
        .split(';')
        .map((pair) => pair.trim())
        .find((pair) => pair.startsWith(prefix));

    if (entry === undefined) {
        return null;
    }
    try {
        return normalizeStoreOverride(decodeURIComponent(entry.slice(prefix.length)));
    } catch {
        // A malformed percent-sequence must not take getStoreDomain() down with it.
        return null;
    }
};

/** The remembered store on the client, or null where overriding is not allowed. */
export const getStoreOverride = (): string | null => {
    if (typeof document === 'undefined' || !isStoreOverrideEnvironmentAllowed()) {
        return null;
    }
    return readStoreOverrideFromCookieHeader(document.cookie);
};
